import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Product Copilots\npageTitle: Creating product copilots - Mendable\ndescription: Creating product copilots with Mendable.\n---\n\n## What is a product copilot?\n\nA product copilot is a Mendable component that is trained to answer questions about your product that take in dynamic application context. It is trained on your product's documentation, and can be deployed anywhere within your App.\n\n## What's the difference between a copilot and a normal QA AI?\n\nApplication context. With a copilot you can feed users' details, page details of the application, and other dynamic information to the AI to get personalized answers.\n\n## Implementing a product copilot\nBefore install, make sure you have either signed up through our [platform](https://mendable.ai) or we have provided you with the `ANON_KEY`. Without the key, you won't be able to setup the search component.\n\n### 1. Importing a component\n\nImport any component into your React application. For this example let's suppose the component is inside the **user settings page** of an application.\n\n```bash\nnpm i @mendable/search@latest\n```\n\n```jsx\nimport { MendableFloatingButton } from \"@mendable/search\"\n\nconst style = { darkMode: false, accentColor: \"#123456\" }\n\nconst floatingButtonStyle = {\n    color: \"#fff\",\n    backgroundColor: \"#123456\"\n}\n\n<MendableFloatingButton \n    anon_key='YOUR_ANON_KEY'\n    style={style}\n    floatingButtonStyle={floatingButtonStyle} />\n\n```\n\n### 2. Passing application context\n\nTo pass in dynamic context to the AI, we can use the `context` parameter in the component. Here you can send details such as the user settings so the AI is aware of it.\n\n```jsx\nimport { MendableFloatingButton } from \"@mendable/search\"\n\nconst style = { darkMode: false, accentColor: \"#123456\" }\n\nconst floatingButtonStyle = {\n    color: \"#fff\",\n    backgroundColor: \"#123456\"\n}\n\n<MendableFloatingButton \n    anon_key='YOUR_ANON_KEY'\n    style={style}\n    floatingButtonStyle={floatingButtonStyle}\n    context={\n        \"Here are user details to help you answer user's specific questions: \"\n        + \"user_id: \" + user.id,  + \"\\n\"\n        + \"user_name: \" + user.name,  + \"\\n\"\n        + \"user_email: \" + user.email,  + \"\\n\"\n        + \"project_id: \" + project.id,  + \"\\n\"\n    } />\n\n```\n\n### 3. That's it!\n\nPlay around with the component and see how useful it is for your use case. You can put the component in multiple pages to have multiple additional contexts and leverage page information to get personalized answers.\n\n![](/images/copilot.png)\n\n\n\n";
const filepath = "/applications/copilots.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
